import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import type {
  Reference,
  ReferenceCollection,
  ReferenceListResponse,
} from "@/types/reference.type";

/**
 * Cache surgery for the wall. A reference can sit in several cache entries at
 * once (the infinite wall, one entry per picked kind in a fan-out, a collection's
 * own wall), in two shapes: a list answer, or the infinite query's pages. These
 * walk every entry under `["references", orgId]` so one optimistic write moves
 * the reference everywhere it is drawn.
 */

type ListShape = ReferenceListResponse | InfiniteData<ReferenceListResponse> | undefined;

const isInfinite = (data: ListShape): data is InfiniteData<ReferenceListResponse> =>
  !!data && "pages" in data;

function mapLists(
  qc: QueryClient,
  orgId: string | null,
  fn: (list: ReferenceListResponse) => ReferenceListResponse,
) {
  qc.setQueriesData<ListShape>({ queryKey: ["references", orgId] }, (data) => {
    if (!data) return data;
    if (isInfinite(data)) return { ...data, pages: data.pages.map(fn) };
    if (!("references" in data)) return data;
    return fn(data);
  });
}

export function cancelReferenceFetches(qc: QueryClient, orgId: string | null) {
  return qc.cancelQueries({ queryKey: ["references", orgId] });
}

/** The reference as the cache holds it now (the rollback copy for one row). */
export function readReference(
  qc: QueryClient,
  orgId: string | null,
  referenceId: string,
): Reference | undefined {
  for (const [, data] of qc.getQueriesData<ListShape>({ queryKey: ["references", orgId] })) {
    if (!data) continue;
    const lists = isInfinite(data) ? data.pages : "references" in data ? [data] : [];
    for (const list of lists) {
      const hit = list.references.find((r) => r.id === referenceId);
      if (hit) return hit;
    }
  }
  return undefined;
}

/** Patch one reference wherever it is drawn. */
export function patchReference(
  qc: QueryClient,
  orgId: string | null,
  referenceId: string,
  patch: (reference: Reference) => Reference,
) {
  mapLists(qc, orgId, (list) => ({
    ...list,
    references: list.references.map((r) => (r.id === referenceId ? patch(r) : r)),
  }));
}

/** Put references onto one collection's wall (filing them in). No-op when that
 *  wall has not been read yet; opening it fetches. */
export function addReferences(
  qc: QueryClient,
  orgId: string | null,
  collectionId: string,
  references: Reference[],
) {
  if (references.length === 0) return;
  for (const [key, data] of qc.getQueriesData<ListShape>({ queryKey: ["references", orgId] })) {
    if (!data || !isCollectionWall(key, collectionId)) continue;
    const insert = (list: ReferenceListResponse, count: boolean) => {
      const existing = new Set(list.references.map((r) => r.id));
      const added = references.filter((r) => !existing.has(r.id));
      if (added.length === 0) return list;
      return {
        ...list,
        references: [...added, ...list.references],
        total: count ? list.total + added.length : list.total,
      };
    };
    if (isInfinite(data)) {
      qc.setQueryData(key, {
        ...data,
        pages: data.pages.map((page, index) => insert(page, index === 0)),
      });
    } else if ("references" in data) {
      qc.setQueryData(key, insert(data, true));
    }
  }
}

/** The collection wall is stale: the next time it is on screen it is read again. */
export function invalidateCollectionWall(qc: QueryClient, orgId: string | null, collectionId: string) {
  return qc.invalidateQueries({
    queryKey: ["references", orgId],
    predicate: (query) => isCollectionWall(query.queryKey, collectionId),
  });
}

/** Take references off every wall (delete, or leaving the open collection). */
export function removeReferences(
  qc: QueryClient,
  orgId: string | null,
  referenceIds: string[],
  only?: (queryKey: readonly unknown[]) => boolean,
) {
  const ids = new Set(referenceIds);
  for (const [key, data] of qc.getQueriesData<ListShape>({ queryKey: ["references", orgId] })) {
    if (!data || (only && !only(key))) continue;
    const strip = (list: ReferenceListResponse) => {
      const kept = list.references.filter((r) => !ids.has(r.id));
      return { ...list, references: kept, total: list.total - (list.references.length - kept.length) };
    };
    if (isInfinite(data)) qc.setQueryData(key, { ...data, pages: data.pages.map(strip) });
    else if ("references" in data) qc.setQueryData(key, strip(data));
  }
}

/* ------------------------------------------------------------ collections */

export const collectionsKey = (orgId: string | null) => ["reference-collections", orgId];

export function readCollections(qc: QueryClient, orgId: string | null) {
  return qc.getQueryData<ReferenceCollection[]>(collectionsKey(orgId));
}

export function patchCollection(
  qc: QueryClient,
  orgId: string | null,
  collectionId: string,
  patch: (collection: ReferenceCollection) => ReferenceCollection,
) {
  qc.setQueryData<ReferenceCollection[]>(collectionsKey(orgId), (list) =>
    list?.map((c) => (c.id === collectionId ? patch(c) : c)),
  );
}

/** True when this cache key is a read of one collection's wall. */
export function isCollectionWall(key: readonly unknown[], collectionId: string) {
  return key.some(
    (part) =>
      !!part && typeof part === "object" && (part as { collectionId?: string }).collectionId === collectionId,
  );
}
